/**
 * ProductCard — iter27 redesign.
 *
 * - Visible "Add" button that opens a bottom-sheet for size selection. After
 *   Confirm, a success toast appears and the card flips into a [- count +]
 *   counter sourced from the actual cart (not local state) so + and - keep
 *   the storefront, cart badge and product card in lock-step.
 * - When count reaches 0, the cart line item is REMOVED (CartContext does
 *   this via updateQty <= 0 → remove).
 * - Counter is rendered fully inside the card with compact sizing so it
 *   never overflows on small phones.
 * - Scroll position is preserved across the sheet open/close because we
 *   never navigate — only render a fixed overlay.
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useSettings } from "../contexts/SettingsContext";
import { imageUrl } from "../lib/cloudinary";
import { Plus, Minus, X } from "lucide-react";
import { toast } from "sonner";
import { trackAddToCart } from "../lib/metaPixel";
import { pushAddToCart, pushSelectItem } from "../lib/gtmDataLayer";
import { productUrl } from "../lib/seo";

/**
 * VariantTransition — premium 4-mode image transition layer used by every
 * product card.  Images are absolutely positioned over a fixed-ratio
 * container and switched by mutating opacity / transform.  Underlying
 * <img> elements all use `object-contain` so the product photo is
 * NEVER cropped, zoomed past natural ratio, or skewed.
 *
 *   - "slideshow"        : smooth cross-fade (no scale)
 *   - "scale"            : outgoing 1→1.05 fade-out, incoming 0.95→1 fade-in
 *   - "scale_kenburns"   : "scale" + slow ken-burns drift on the active image
 *   - "coverflow"        : 3D cover-flow (prev/center/next with rotateY+scale)
 *
 * The 3-second hold is enforced by the parent.  Transition duration here is
 * ~700 ms.  Layout-impacting styles (background) are unchanged from the
 * original card so no other surface re-flows.
 *
 * iter92 — Cloudinary optimisation.  Previously we mounted every variant
 * image up-front, so scrolling past a 63-product /shop grid immediately
 * fetched 315+ Cloudinary derivations (5 variants × 63 products) even
 * though only ONE image per card is visible at a time.  Now we track
 * `mountedIdx` and only render `<img>` for indices that have been
 * activated at least once (plus the next one to pre-warm the cross-fade).
 * The `<div>` slot layout stays identical so animations are pixel-for-pixel
 * unchanged.
 */
function VariantTransition({ images, activeIdx, type, productId, alt, isVisible = true }) {
  const [mountedSet, setMountedSet] = useState(() => new Set([0]));
  useEffect(() => {
    if (!isVisible) return;
    // Pre-warm the NEXT slide too so the next cross-fade is buttery.
    const next = (activeIdx + 1) % images.length;
    setMountedSet((prev) => {
      if (prev.has(activeIdx) && prev.has(next)) return prev;
      const s = new Set(prev);
      s.add(activeIdx);
      s.add(next);
      return s;
    });
  }, [activeIdx, images.length, isVisible]);

  if (type === "coverflow") {
    // Coverflow inherently needs prev/center/next visible at once;
    // still let it use the same mount-gate to avoid loading the whole tail.
    return <CoverFlowTransition images={images} activeIdx={activeIdx} productId={productId} alt={alt} mountedSet={mountedSet} />;
  }
  const isScale = type === "scale" || type === "scale_kenburns";
  const isKenBurns = type === "scale_kenburns";
  return (
    <div
      className="absolute inset-0"
      data-testid={`card-slideshow-${productId}`}
      data-transition={type}
    >
      {images.map((src, i) => {
        const active = i === activeIdx;
        const mounted = mountedSet.has(i);
        let transform = "scale(1)";
        if (isScale) {
          transform = active ? "scale(1)" : "scale(1.05)";
        }
        return (
          <div
            key={src + i}
            className="absolute inset-0 will-change-[opacity,transform]"
            style={{
              opacity: active ? 1 : 0,
              transform,
              transitionProperty: "opacity, transform",
              transitionDuration: "700ms",
              transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            {mounted ? (
              <img
                src={imageUrl(src, { width: 600, height: 600, crop: "fill" })}
                alt={alt}
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
                draggable={false}
                className={`w-full h-full object-cover select-none ${
                  isKenBurns && active ? "vt-kenburns" : ""
                }`}
                data-testid={i === 0 ? `card-image-${productId}` : undefined}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function CoverFlowTransition({ images, activeIdx, productId, alt, mountedSet }) {
  // Render the three nearest slides (prev, center, next).  Side slides are
  // rotated on the Y-axis and scaled down to ~0.82, center sits flat at 1.0
  // and full opacity.  No clipping — outer container has overflow-hidden
  // but slides only translate ~30% so they peek in from the edges.
  const n = images.length;
  const prev = (activeIdx - 1 + n) % n;
  const next = (activeIdx + 1) % n;
  const slides = [
    { src: images[prev], pos: -1, key: `p-${prev}`, idx: prev },
    { src: images[activeIdx], pos: 0, key: `c-${activeIdx}`, idx: activeIdx },
    { src: images[next], pos: 1, key: `n-${next}`, idx: next },
  ];
  return (
    <div
      className="absolute inset-0"
      style={{ perspective: "1100px" }}
      data-testid={`card-slideshow-${productId}`}
      data-transition="coverflow"
    >
      {slides.map(({ src, pos, key, idx }) => {
        const isCenter = pos === 0;
        const transform = isCenter
          ? "translateX(0) translateZ(0) rotateY(0deg) scale(1)"
          : `translateX(${pos * 32}%) translateZ(-80px) rotateY(${pos * -28}deg) scale(0.82)`;
        const shouldMount = mountedSet ? mountedSet.has(idx) : true;
        return (
          <div
            key={key}
            className="absolute inset-0 will-change-transform"
            style={{
              transform,
              opacity: isCenter ? 1 : 0.55,
              zIndex: isCenter ? 2 : 1,
              transitionProperty: "transform, opacity",
              transitionDuration: "700ms",
              transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            {shouldMount ? (
              <img
                src={imageUrl(src, { width: 600, height: 600, crop: "fill" })}
                alt={alt}
                loading="lazy"
                decoding="async"
                draggable={false}
                className="w-full h-full object-cover select-none"
                data-testid={isCenter ? `card-image-${productId}` : undefined}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function SizeSheet({ product, onClose, onConfirm }) {
  // Show every variant — iter29 removed the ≤₹15 sample filter so the
  // bottom sheet matches PDP and the canonical price list exactly.
  const variants = useMemo(() => product.variants || [], [product.variants]);
  // iter99 — Combos have no size choice; the popup should instead show
  // the products included in the combo, sourced from the SAME backend
  // ``combo_items_resolved`` field used by the Product Detail Page so
  // the two views can never drift apart.  We prefer the enriched list
  // (with real product names) and gracefully fall back to the raw
  // references if the backend hasn't enriched them for any reason.
  const comboItems = useMemo(
    () => product.combo_items_resolved || product.combo_items || [],
    [product.combo_items_resolved, product.combo_items],
  );
  const isCombo = (product.category === "Combos") || comboItems.length > 0;

  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const handleConfirm = () => {
    // For combos, there is no size selection — pass the (usually
    // single) variant unchanged so the existing cart-add flow keeps
    // working identically.
    const v = isCombo ? (variants[0] || null) : (variants[selectedIdx] || null);
    onConfirm(v);
  };

  return (
    <div
      className="fixed inset-0 z-[160] flex items-end sm:items-center justify-center"
      data-testid="size-sheet"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-brand-dark/55 backdrop-blur-sm"
        data-testid="size-sheet-backdrop"
      />
      <div className="relative w-full sm:max-w-md bg-brand-cream rounded-t-3xl sm:rounded-3xl shadow-2xl border border-brand-line max-h-[88vh] flex flex-col">
        <div className="flex items-start justify-between px-6 pt-6 pb-3">
          <div>
            {!isCombo && (
              <span className="overline text-[10px] !text-brand-muted block mb-1">Choose a size</span>
            )}
            <h3 className="font-serif text-2xl text-brand-dark leading-tight pr-6">{product.name}</h3>
            {isCombo && (
              <span className="overline text-[10px] !text-brand-muted block mt-2">Products Included</span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white border border-brand-line text-brand-dark flex items-center justify-center hover:bg-brand-subtle"
            aria-label="Close size selection"
            data-testid="size-sheet-close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto">
          {isCombo ? (
            <ul
              className="space-y-2 text-sm text-brand-dark"
              data-testid="combo-included-list"
            >
              {comboItems.length === 0 ? (
                <li className="text-brand-muted italic">Contents not configured yet.</li>
              ) : (
                comboItems.map((ci, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 px-4 py-2.5 rounded-xl bg-white border border-brand-line"
                    data-testid={`combo-included-${i}`}
                  >
                    <span className="text-brand-chili mt-0.5">•</span>
                    <div className="flex-1">
                      <span className="font-medium">
                        {ci.name || ci.product_name || "Product"}
                      </span>
                      {ci.variant_label && (
                        <span className="text-brand-muted"> — {ci.variant_label}</span>
                      )}
                      {ci.quantity && Number(ci.quantity) > 1 && (
                        <span className="text-brand-muted"> × {ci.quantity}</span>
                      )}
                    </div>
                  </li>
                ))
              )}
            </ul>
          ) : (
            <div className="space-y-2">
              {variants.map((v, i) => (
                <button
                  key={v.label || i}
                  type="button"
                  onClick={() => setSelectedIdx(i)}
                  className={`w-full flex items-center justify-between px-5 py-3.5 rounded-xl border transition-all text-left ${
                    selectedIdx === i
                      ? "bg-brand-dark text-white border-brand-dark"
                      : "bg-white text-brand-dark border-brand-line hover:border-brand-chili/50"
                  }`}
                  data-testid={`size-option-${i}`}
                >
                  <span className="font-medium">{v.label}</span>
                  <span className={`tabular-nums text-sm ${selectedIdx === i ? "text-white/90" : "text-brand-muted"}`}>
                    ₹{Number(v.price).toFixed(0)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 pb-7 pt-3 flex justify-end">
          <button
            type="button"
            onClick={handleConfirm}
            className="inline-flex items-center gap-2 bg-brand-chili text-brand-cream font-medium rounded-full px-7 py-3 text-sm transition-transform active:scale-[0.98] hover:scale-[1.02]"
            data-testid="size-sheet-confirm"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProductCard({ p, index = 0 }) {
  const { add, updateQty, items: cartItems } = useCart();
  const { settings } = useSettings();
  const [sheetOpen, setSheetOpen] = useState(false);

  // ───── Variant slideshow ──────────────────────────────────────────────
  // Build the cycle of images from the variants in ascending price order.
  // Per iter35, the cycle starts at the LOWEST variant image (35g first)
  // and walks up to the highest, then loops back.  Main image is used only
  // when no variant has an image.
  const slideshow = useMemo(() => {
    const variantImgs = (p.variants || [])
      .slice()
      .sort((a, b) => (a.price || 0) - (b.price || 0))
      .map((v) => v.image_url)
      .filter(Boolean);
    const seen = new Set();
    const dedup = variantImgs.filter((src) => {
      if (seen.has(src)) return false;
      seen.add(src);
      return true;
    });
    if (dedup.length) return dedup;
    return p.image_url ? [p.image_url] : [];
  }, [p.image_url, p.variants]);

  const slideshowEnabled = settings?.product_card_slideshow_enabled !== false
    && slideshow.length > 1;
  const transitionType = settings?.product_card_transition_type || "scale_kenburns";
  const [frameIdx, setFrameIdx] = useState(0);
  const timerRef = useRef(null);
  const cardRef = useRef(null);
  // iter92 — Only run the slideshow interval AND load additional variant
  // images when the card is actually intersecting the viewport.  Prior
  // behaviour started every card's interval on mount and pre-loaded all
  // variant images, so scrolling past a 63-item grid still ticked 63
  // timers and fetched 300+ Cloudinary derivations.  Now, off-screen
  // cards stay frozen on frame 0 with a single image loaded.
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!cardRef.current) return undefined;
    if (typeof IntersectionObserver === "undefined") { setInView(true); return undefined; }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { setInView(true); break; }
        }
      },
      { rootMargin: "200px 0px" }
    );
    io.observe(cardRef.current);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!slideshowEnabled || !inView) return undefined;
    // 3s display per variant (per iter36 spec)
    timerRef.current = setInterval(() => {
      setFrameIdx((i) => (i + 1) % slideshow.length);
    }, 3000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [slideshowEnabled, slideshow.length, inView]);

  const heroImg = slideshowEnabled ? slideshow[frameIdx] : (slideshow[0] || p.image_url);

  const hasVariants = p.variants && p.variants.length > 0;
  // Show ALL real variants in price/size labels — no minimum-price filter
  // any more.  ProductCard now mirrors PDP / size-sheet exactly.
  const displayVariants = hasVariants ? p.variants : [];
  const basePrice = hasVariants
    ? Math.min(...p.variants.map((v) => v.price))
    : p.price;
  const sizeCount = displayVariants.length;

  // Sum quantity for this product across all its variant lines in cart.
  // Single source of truth — keeps card counter in lock-step with the cart
  // badge and the line items on /cart.
  const productLines = cartItems.filter((it) => it.product_id === p.product_id);
  const count = productLines.reduce((s, it) => s + it.quantity, 0);
  const lastLine = productLines.length ? productLines[productLines.length - 1] : null;

  const openSheet = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!p.in_stock) return;
    setSheetOpen(true);
  };

  const onMinus = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!lastLine) return;
    // updateQty with newQty<=0 removes the line item entirely from cart.
    updateQty(lastLine.product_id, lastLine.variant_label, lastLine.quantity - 1);
  };

  const handleConfirm = (variant) => {
    add(p, 1, variant);
    trackAddToCart(p, variant, 1);
    pushAddToCart(p, variant, 1);
    setSheetOpen(false);
    toast.success("The Product has successfully been added to your cart", {
      duration: 2200,
    });
  };

  return (
    <>
      <Link
        ref={cardRef}
        to={productUrl(p)}
        onClick={() => pushSelectItem(p, { listName: p.category || "Products" })}
        className="product-card group relative block overflow-hidden"
        data-testid={`product-card-${p.product_id}`}
      >
        <div className="relative aspect-square overflow-hidden bg-brand-cream">
          {slideshowEnabled ? (
            <VariantTransition
              images={slideshow}
              activeIdx={frameIdx}
              type={transitionType}
              productId={p.product_id}
              alt={p.name}
              isVisible={inView}
            />
          ) : heroImg ? (
            <img
              src={imageUrl(heroImg, { width: 600, height: 600, crop: "fill" })}
              alt={p.name}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              data-testid={`card-image-${p.product_id}`}
            />
          ) : (
            // iter70 — genuine "No Image" placeholder for products (mainly
            // Combos) awaiting admin photo upload.
            <div
              className="absolute inset-0 flex items-center justify-center bg-brand-subtle text-brand-muted text-xs uppercase tracking-wider"
              data-testid={`card-image-placeholder-${p.product_id}`}
            >
              No Image
            </div>
          )}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {p.is_bestseller && <span className="badge-bestseller" data-testid="badge-bestseller">Bestseller</span>}
            {!p.in_stock && <span className="badge-out" data-testid="badge-out-of-stock">Out of stock</span>}
          </div>
        </div>

        <div className="p-4 sm:p-5 flex flex-col gap-1 border-t border-brand-line/60">
          <span className="overline text-[10px]">{p.category}</span>
          <h3 className="font-serif text-base sm:text-xl text-brand-dark leading-tight line-clamp-2">{p.name}</h3>

          <div className="flex items-center justify-between gap-2 mt-2 min-w-0">
            <div className="flex flex-col min-w-0">
              {/* iter68 — MRP + selling price + savings display.  Only
                   renders when `p.mrp` is set AND greater than the
                   selling price (mandatory for Combos, optional and
                   currently unused for every other category). */}
              {p.mrp && p.mrp > basePrice ? (
                <>
                  <span className="text-[11px] text-brand-muted truncate line-through" data-testid={`product-mrp-${p.product_id}`}>
                    MRP ₹{Number(p.mrp).toFixed(0)}
                  </span>
                  <span className="text-base font-semibold text-brand-chili truncate" data-testid={`product-price-${p.product_id}`}>
                    ₹{basePrice.toFixed(0)}
                  </span>
                  <span className="text-[11px] font-medium text-brand-success truncate" data-testid={`product-savings-${p.product_id}`}>
                    You Save ₹{Number(p.mrp - basePrice).toFixed(0)}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-base font-semibold text-brand-dark truncate" data-testid={`product-price-${p.product_id}`}>
                    {hasVariants && <span className="text-xs text-brand-muted font-normal mr-1">from</span>}
                    ₹{basePrice.toFixed(0)}
                  </span>
                  <span className="text-[11px] text-brand-muted truncate">
                    {hasVariants ? `${sizeCount} ${sizeCount === 1 ? "size" : "sizes"}` : p.weight}
                  </span>
                </>
              )}
            </div>

            {/* Visible action: Add button OR counter once at least one was added. */}
            {count === 0 ? (
              <button
                type="button"
                onClick={openSheet}
                disabled={!p.in_stock}
                className="shrink-0 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-brand-cream/70 text-brand-chili text-sm font-bold border border-brand-chili/30 hover:bg-white hover:border-brand-chili transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`card-add-${p.product_id}`}
                aria-label={`Add ${p.name} to cart`}
              >
                Add
              </button>
            ) : (
              <div
                className="shrink-0 inline-flex items-center gap-1 rounded-lg bg-brand-chili text-brand-cream px-1 py-1"
                data-testid={`card-counter-${p.product_id}`}
              >
                <button
                  type="button"
                  onClick={onMinus}
                  className="w-6 h-6 sm:w-7 sm:h-7 rounded-md flex items-center justify-center hover:bg-white/15 transition-colors"
                  aria-label="Decrease quantity"
                  data-testid={`card-minus-${p.product_id}`}
                >
                  <Minus className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={2.2} />
                </button>
                <span className="min-w-[1.1rem] text-center text-xs sm:text-sm font-semibold tabular-nums" data-testid={`card-count-${p.product_id}`}>{count}</span>
                <button
                  type="button"
                  onClick={openSheet}
                  className="w-6 h-6 sm:w-7 sm:h-7 rounded-md flex items-center justify-center hover:bg-white/15 transition-colors"
                  aria-label="Add another"
                  data-testid={`card-plus-${p.product_id}`}
                >
                  <Plus className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={2.2} />
                </button>
              </div>
            )}
          </div>
        </div>
      </Link>

      {sheetOpen && (
        <SizeSheet
          product={p}
          onClose={() => setSheetOpen(false)}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}
